import React from 'react';
import { colors } from '../styles/tokens';
import { useEsMovil } from '../hooks/useEsMovil';

/**
 * Caja de "cargando…" / "no se pudo cargar…" del tablero, la MISMA en Global,
 * Tienda y Presupuesto (SPEC `2026-10-12-tableros-homologados.md`, «Estados de
 * carga y error»).
 *
 * En teléfono baja el relleno (28 px de aire arriba y abajo para UNA línea de
 * texto se comen un tercio de la pantalla) y unifica el hueco entre bloques a
 * 12 px. En escritorio son los valores de siempre.
 */
export const cajaEstado = (esMovil: boolean, marginTop: number): React.CSSProperties => ({
  marginTop: esMovil ? 12 : marginTop,
  background: colors.card,
  border: `1px solid ${colors.border}`,
  borderRadius: 16,
  padding: esMovil ? '20px 14px' : '28px 20px',
  textAlign: 'center',
  fontWeight: 600,
  fontSize: 13,
});

export interface EstadoCargaProps {
  testid: string;
  texto: string;
  marginTop?: number;
}

/**
 * Indicador de carga. Ocupa el lugar del contenido: mientras llega lo nuevo NO
 * se pintan los datos viejos debajo de los rótulos nuevos (una semana con las
 * cifras del mes anterior se lee sin notarlo).
 */
export const EstadoCarga: React.FC<EstadoCargaProps> = ({ testid, texto, marginTop = 14 }) => {
  const esMovil = useEsMovil();
  return (
    <div
      data-testid={testid}
      role="status"
      aria-live="polite"
      style={{ ...cajaEstado(esMovil, marginTop), color: colors.texto.tertiary }}
    >
      {texto}
    </div>
  );
};

export interface EstadoErrorProps {
  testid: string;
  /** Qué no se pudo cargar: «No se pudo cargar el periodo». */
  titulo: string;
  /** El mensaje de la API (los 400 de validación ya vienen en español). */
  mensaje: string;
  onReintentar: () => void;
  /**
   * `true` = la API rechazó el periodo (400). Se dice «Revisa el periodo» y NO
   * se ofrece «Reintentar», porque la misma consulta volvería a fallar (mismo
   * criterio que la app).
   */
  validacion?: boolean;
  marginTop?: number;
}

/**
 * Error con «Reintentar». Antes cada bloque tenía el suyo y ninguno dejaba
 * reintentar: la tienda leía «No se pudo cargar la tienda: Internal server
 * error» delante de un tablero vacío y la única salida era recargar la página
 * (y perder los filtros). Un error NUNCA se pinta como «sin datos».
 */
export const EstadoError: React.FC<EstadoErrorProps> = ({
  testid,
  titulo,
  mensaje,
  onReintentar,
  validacion = false,
  marginTop = 14,
}) => {
  const esMovil = useEsMovil();
  return (
    <div
      data-testid={testid}
      data-validacion={validacion ? 'si' : undefined}
      role="alert"
      style={{
        ...cajaEstado(esMovil, marginTop),
        color: colors.rojo,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span>
        {validacion ? 'Revisa el periodo' : titulo}: {mensaje}
      </span>
      {!validacion && (
        <button
          type="button"
          data-testid={`${testid}-reintentar`}
          onClick={onReintentar}
          style={{
            border: `1px solid ${colors.brandSoftBorder}`,
            background: colors.brandSoftBg,
            color: colors.brand,
            borderRadius: 999,
            padding: esMovil ? '0 20px' : '7px 18px',
            minHeight: esMovil ? 44 : undefined,
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 800,
            cursor: 'pointer',
          }}
        >
          Reintentar
        </button>
      )}
    </div>
  );
};
